import React, { useState, useEffect, useRef } from 'react';
import './Project.css';

// Import the images
import alzhnet from '../Alzhnet.png'; // Update the path as necessary
import DDL from '../DDL.png'; // Update the path for PharmEasy logo
import mednet from '../mednet.png'; // Update the path for Caterpillar logo
import svr from '../svr.jpg';
import ansieyes from '../ansieyes.png';
import yonakh from '../yonakh.png';
import github from '../github.png';
import research from '../research.png'
interface ProjectItem {
  year: string;
  role: string;
  description: string[]; // Change description to an array of strings
  technologies: string[];
  logo: string;
  ref: string;
}

const PROJECTS: ProjectItem[] = [
  {
    year: "JUL 2024",
    role: "MRI-based Automated Diagnosis of Alzheimer’s Disease using Alzh-Net Deep Learning Model",
    description: [
      'Developed a CNN-based model, Alzh-Net, to aid in diagnosing and segmenting Alzheimer’s disease symptoms through image analysis.',
      "Designed and implemented a dual CNN architecture, Alzh-Net Upper and Lower, for multi-scale feature extraction.",
      "Captured global patterns and broad perspectives in input images by employing convolutional layers with varied strides and max-pooling layers.",
    ],
    technologies: ["Python", "TensorFlow", "Keras", "SkLearn", 'Pandas', 'Numpy'],
    logo: alzhnet, // Use the imported logo,
    ref: "https://doi.org/10.1016/j.bspc.2024.107367"
  },
  {
    year: "2025",
    role: "Ansieyes - AI-Powered GitHub Bot",
    description: [
      "Built an AI-powered bot for automated PR reviews and intelligent issue triage using Google's Gemini AI.",
      "Implemented two-pass analysis (Librarian + Surgeon) for context-aware issue understanding with token optimization via Repomix.",
      "Added prompt injection detection, duplicate issue detection, and auto-labeling capabilities for secure and efficient workflows.",
    ],
    technologies: ["Python", "Gemini AI", "GitHub Actions", "Flask", "Docker", "Shell"],
    logo: ansieyes,
    ref: "https://github.com/ansible-network/Ansieyes"
  },
  {
    year: "2025",
    role: "Yonakh - AI Memory Server for Software Engineering",
    description: [
      "Built a local-first, vendor-neutral AI memory server that provides a shared knowledge layer for AI coding tools (Claude, Cursor, Copilot).",
      "Implemented a knowledge graph with 21 entity types and relationships, hybrid search (FTS5 + semantic via sqlite-vec), and automated rules engine for inference.",
      "Features include memory quality engine with importance scoring, near-duplicate detection, conflict detection, temporal decay, and full AI provenance with time-travel queries.",
    ],
    technologies: ["Python", "FastAPI", "SQLite", "MCP", "React", "TypeScript"],
    logo: yonakh,
    ref: "https://github.com/svenkat19/yonakh"
  },
  {
    year: "DEC 2022",
    role: "Data-Efficient Deep Learning Framework for Detecting Cloudy Vision in Human Eye",
    description: [
      "Understanding existing models for cataract detection.",
      "Developed a model to improve diagnosis of cataract with higher accuracy with a dataset of over 10000 images",
    ],
    technologies: ["Python", "Sklearn", "Tensorflow", "OpenCV", "Pandas", "NumPy"],
    logo: DDL, // Use the imported logo
    ref: "https://github.com/svenkat19/Data-Efficient-Deep-Learning-Framework-for-Detecting-Cloudy-Vision-in-Human-Eye"
  },
  {
    year: "DEC 2022",
    role: "Classification of Imbalanced Multivariate Data Using Surface to Volume Multiclass Classifier Tree",
    description: [
      "Extended SVR trees for multiclass datasets.",
      "Proposed a solution to extend SVR trees for Multiclass classification of balanced and imbalanced datasets."
    ],
    technologies: ["Python", "Sklearn", "Pandas", "Numpy"],
    logo: svr, // Use the imported logo
    ref: "https://github.com/svenkat19/Classification-of-Imbalanced-Multivariate-Data-using-Surface-to-Volume-Multiclass-Classifier-Tree"
  },
  {
    year: "NOV 2022",
    role: "MedNet Medical Platform",
    description: [
      "Developed a web platform for the health system providing online video call consultations with doctors.",
      "Implemented real-time hospital search based on location and bed availability and promoted digital record keeping to reduce paper usage."
    ],
    technologies: ["EJS", "NodeJS", "ExpressJS", "MongoDB", "MySQL", "PeerJS"],
    logo: mednet, // Use the imported logo
    ref: "https://github.com/svenkat19/MedNet"
  },
];

const Project: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const projectRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = projectRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return (
    <section className="project-section" ref={projectRef}>
      <h2 className={`project-heading ${isVisible ? 'fade-in' : 'fade-out'}`}>Projects</h2>
      <div className={`project-list ${isVisible ? 'fade-in' : 'fade-out'}`}>
        {PROJECTS.map((proj, index) => (
          <div key={index} className={`project-item ${isVisible ? 'fade-in' : 'fade-out'}`}>
            <div className="project-year">
              <img src={proj.logo} alt={`${proj.logo} Logo`} className="project-logo" />
            </div>
            <div className="project-details">
              <h2 className="project-role">
                {proj.role} <br />
              </h2>
              <ul className="project-description">
                {proj.description.map((desc, idx) => (
                  <li key={idx}>{desc}</li>  // Render each description as a list item
                ))}
              </ul>
              <div className="project-technologies">
                {proj.technologies.map((tech, idx) => (
                  <span key={idx} className="technology-badge">{tech}</span>
                ))}
              </div>
              <div className="project-link">
                <a href={proj.ref} target="_blank" rel="noopener noreferrer">
                  <img className="ref-link" src={proj.logo===alzhnet?research:github} alt="Link" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Project;
